import {
    Component,
    input,
    output,
    signal,
    effect,
    ViewChild,
    TemplateRef,
    computed,
    model,
    OnInit,
    OnDestroy,
    forwardRef,
    inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { DatePicker } from 'primeng/datepicker';
import {
    DatePickerConfig,
    DatePickerSelectEvent,
    DatePickerChangeEvent,
    DatePickerClearEvent,
    DatePickerInputEvent,
    DatePickerTodayClickEvent,
    DatePickerCloseEvent,
    DatePickerMonthChangeEvent,
    DatePickerYearChangeEvent,
    DateTemplateContext,
} from './datepicker.model';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TextCasePipe } from 'src/app/@shared/pipes/textCase.pipe';

/**
 * DatePicker Component
 * 
 * A comprehensive datepicker component that supports:
 * - Single date selection
 * - Date range selection
 * - Multiple date selection
 * - Time selection
 * - DateTime selection
 * - Inline/Overlay modes
 * - Min/max dates
 * - Disabled dates and days
 * - Custom templates
 * - Touch UI support
 * - Month/Year navigation
 * 
 * This component wraps PrimeNG DatePicker with additional features
 * and follows Angular Signals pattern with ControlValueAccessor for form integration.
 * 
 * @example
 * // Basic usage
 * <app-datepicker [(value)]="selectedDate" />
 * 
 * @example
 * // Date range with time
 * <app-datepicker 
 *   [(value)]="dateRange"
 *   [config]="{
 *     selectionMode: 'range',
 *     showTime: true,
 *     showButtonBar: true
 *   }"
 * />
 * 
 * @example
 * // Multiple dates with restrictions
 * <app-datepicker 
 *   [(value)]="selectedDates"
 *   [config]="{
 *     selectionMode: 'multiple',
 *     minDate: minDate,
 *     maxDate: maxDate,
 *     disabledDays: [0, 6] // Disable weekends
 *   }"
 * />
 */
@Component({
    selector: 'app-datepicker',
    standalone: true,
    imports: [CommonModule, FormsModule, DatePicker],
    templateUrl: './datepicker.component.html',
    styleUrls: ['./datepicker.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DatepickerComponent),
            multi: true,
        },
        TextCasePipe
    ],
})
export class DatepickerComponent implements OnInit, OnDestroy, ControlValueAccessor {
    /**
     * Model binding for selected date value(s)
     * Can be a single Date, Date[] for range/multiple, or null
     */
    value = model<Date | Date[] | null>(null);

    /**
     * Configuration object for the datepicker
     * See DatePickerConfig interface for all available options
     */
    config = input<DatePickerConfig>({});

    /**
     * Custom date cell template
     * Allows customization of individual date cells in the calendar
     */
    dateTemplate = input<TemplateRef<DateTemplateContext> | null>(null);

    /**
     * Custom header template
     * Allows customization of the datepicker header
     */
    headerTemplate = input<TemplateRef<any> | null>(null);

    /**
     * Custom footer template
     * Allows customization of the datepicker footer
     */
    footerTemplate = input<TemplateRef<any> | null>(null);

    /**
     * Custom decade template
     * Allows customization of decade view
     */
    decadeTemplate = input<TemplateRef<any> | null>(null);

    // Output events

    /**
     * Emitted when a date is selected
     */
    onSelect = output<DatePickerSelectEvent>();

    /**
     * Emitted when the value changes (can be different from onSelect)
     */
    onChange = output<DatePickerChangeEvent>();

    /**
     * Emitted when the clear button is clicked
     */
    onClear = output<DatePickerClearEvent>();

    /**
     * Emitted when manual input is detected
     */
    onInput = output<DatePickerInputEvent>();

    /**
     * Emitted when the today button is clicked
     */
    onTodayClick = output<DatePickerTodayClickEvent>();

    /**
     * Emitted when the datepicker overlay is closed
     */
    onClose = output<DatePickerCloseEvent>();

    /**
     * Emitted when the datepicker overlay is shown
     */
    onShow = output<void>();

    /**
     * Emitted when the month is changed
     */
    onMonthChange = output<DatePickerMonthChangeEvent>();

    /**
     * Emitted when the year is changed
     */
    onYearChange = output<DatePickerYearChangeEvent>();

    /**
     * Emitted when focus is gained
     */
    onFocus = output<FocusEvent>();

    /**
     * Emitted when focus is lost
     */
    onBlur = output<FocusEvent>();

    // Internal state

    /**
     * Internal loading state
     */
    loading = signal<boolean>(false);

    /**
     * Tracks if the datepicker is visible
     */
    visible = signal<boolean>(false);

    // PrimeNG DatePicker reference
    @ViewChild('datepickerComponent') datepickerComponent: any;

    // Subjects for reactive programming
    private destroy$ = new Subject<void>();

    private textCasePipe = inject(TextCasePipe);

    // ControlValueAccessor implementation
    private onChangeFn: (value: Date | Date[] | null) => void = () => { };
    private onTouchedFn: () => void = () => { };

    /**
     * Computed datepicker configuration
     * Merges default configuration with user-provided config
     */
    datepickerConfig = computed(() => {
        const defaultConfig: DatePickerConfig = {
            placeholder: 'Select a date',
            disabled: false,
            readonly: false,
            showIcon: true,
            iconDisplay: 'button',
            selectionMode: 'single',
            showTime: false,
            timeOnly: false,
            hourFormat: '12',
            dateFormat: 'mm/dd/yy',
            showSeconds: false,
            showMilliseconds: false,
            inline: false,
            view: 'date',
            touchUI: false,
            showButtonBar: false,
            showOnFocus: true,
            numberOfMonths: 1,
            showOtherMonths: true,
            selectOtherMonths: false,
            yearNavigator: false,
            monthNavigator: false,
            firstDayOfWeek: 0,
            showWeek: false,
            appendTo: null,
            todayButtonLabel: 'Today',
            clearButtonLabel: 'Clear',
            showTodayButton: true,
            showClearButton: true,
            keepInvalid: false,
            hideOnDateTimeSelect: true,
            stepHour: 1,
            stepMinute: 1,
            stepSecond: 1,
            stepMillisecond: 1,
            focusTrap: true,
            selectOnFocus: false,
            autofocus: false,

        };
        return { ...defaultConfig, ...this.config() };
    });

    constructor() {
        // Effect to handle value changes
        effect(() => {
            const val = this.value();
            // Additional side effects can be added here if needed
        });
    }

    ngOnInit(): void {
        // Initialize component
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Update the value when ngModel changes
     * @param newValue - The new date value from the DatePicker
     */
    updateValue(newValue: Date | Date[] | null): void {
        // Update the internal value
        this.value.set(newValue);

        // Notify Angular forms of the change
        this.onChangeFn(newValue);
        this.onTouchedFn();
    }

    /**
     * Handle date selection event
     * @param event - The select event from PrimeNG DatePicker
     */
    handleSelect(event: any): void {
        // For PrimeNG DatePicker, the event IS the selected date value
        // It can be a Date, Date[], or null
        const selectedValue = event;

        // Emit the select event
        this.onSelect.emit({
            value: selectedValue,
            originalEvent: undefined, // PrimeNG's new DatePicker doesn't provide originalEvent
        });
    }

    /**
     * Handle value change event
     * @param event - The change event
     */
    handleChange(event: any): void {
        // Update the internal value
        this.value.set(event.value);

        // Notify Angular forms of the change
        this.onChangeFn(event.value);
        this.onTouchedFn();

        // Emit the change event
        this.onChange.emit({
            value: event.value,
            originalEvent: event.originalEvent,
        });
    }

    /**
     * Handle clear event
     * @param event - The clear event
     */
    handleClear(event?: any): void {
        this.value.set(null);
        this.onChangeFn(null);
        this.onTouchedFn();
        this.onClear.emit({
            originalEvent: event,
        });
    }

    /**
     * Handle input event
     * @param event - The input event
     */
    handleInput(event: any): void {
        this.onInput.emit({
            value: event.target.value,
            originalEvent: event,
        });
    }

    /**
     * Handle today button click
     */
    handleTodayClick(): void {
        const today = new Date();
        this.value.set(today);
        this.onChangeFn(today);
        this.onTodayClick.emit({ date: today });
    }

    /**
     * Handle show event
     */
    handleShow(): void {
        this.visible.set(true);
        this.onShow.emit();
    }

    /**
     * Handle close/hide event
     */
    handleClose(event?: any): void {
        this.visible.set(false);
        this.onClose.emit({ event });
    }

    /**
     * Handle month change event
     * @param event - The month change event
     */
    handleMonthChange(event: any): void {
        this.onMonthChange.emit({
            month: event.month,
            year: event.year,
        });
    }

    /**
     * Handle year change event
     * @param event - The year change event
     */
    handleYearChange(event: any): void {
        this.onYearChange.emit({
            month: event.month,
            year: event.year,
        });
    }

    /**
     * Handle focus event
     * @param event - The focus event
     */
    handleFocus(event: FocusEvent): void {
        this.onFocus.emit(event);
    }

    /**
     * Handle blur event
     * @param event - The blur event
     */
    handleBlur(event: FocusEvent): void {
        this.onTouchedFn();
        this.onBlur.emit(event);
    }

    /**
     * Public method to show the datepicker
     */
    show(): void {
        if (this.datepickerComponent) {
            this.datepickerComponent.show();
        }
    }

    /**
     * Public method to hide the datepicker
     */
    hide(): void {
        if (this.datepickerComponent) {
            this.datepickerComponent.hide();
        }
    }

    /**
     * Public method to toggle the datepicker
     */
    toggle(): void {
        if (this.datepickerComponent) {
            this.datepickerComponent.toggle();
        }
    }

    /**
     * Public method to clear the selection
     */
    clear(): void {
        this.handleClear();
    }

    /**
     * Public method to select today's date
     */
    selectToday(): void {
        this.handleTodayClick();
    }

    /**
     * Public method to update the date value programmatically
     * @param date - The date to set
     */
    updateDate(date: Date | Date[] | null): void {
        this.value.set(date);
        this.onChangeFn(date);
    }

    /**
     * Public method to set loading state
     * @param loading - The loading state
     */
    setLoading(loading: boolean): void {
        this.loading.set(loading);
    }

    // ControlValueAccessor methods

    /**
     * Write a new value to the element (called by Angular forms)
     * @param value - The new value
     */
    writeValue(value: Date | Date[] | null): void {
        this.value.set(value);
    }

    /**
     * Register a callback function to be called when the control's value changes
     * @param fn - The callback function
     */
    registerOnChange(fn: (value: Date | Date[] | null) => void): void {
        this.onChangeFn = fn;
    }

    /**
     * Register a callback function to be called when the control is touched
     * @param fn - The callback function
     */
    registerOnTouched(fn: () => void): void {
        this.onTouchedFn = fn;
    }

    /**
     * Set the disabled state of the control
     * @param isDisabled - Whether the control is disabled
     */
    setDisabledState(isDisabled: boolean): void {
        // Note: The disabled state should be managed through the config input
        // This method is required by ControlValueAccessor interface
        // The parent component should update the config.disabled property
    }

    getPlaceholderText(): string {
        return this.datepickerConfig().placeholder ? this.textCasePipe.transform(this.datepickerConfig().placeholder || '') : '';
    }
}

